import type { ReactNode } from 'react';
import { Button, Result, Spin } from 'antd';
import {
  ClockCircleOutlined,
  LockOutlined,
  MailOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ErrorKind } from '@/api/errors';
import { useI18n } from '@/i18n/useI18n';
import { useSession } from '@/session/useSession';
import { Brand } from './Brand';
import { LocaleSwitcher } from './LocaleSwitcher';

/** Map a retryable error kind to its i18n copy base + Result status. */
function errorCopy(kind: ErrorKind): { base: string; status: 'error' | 'warning' } {
  switch (kind) {
    case 'NETWORK':
      return { base: 'error.network', status: 'warning' };
    case 'SERVER':
      return { base: 'error.server', status: 'error' };
    case 'NOT_AVAILABLE':
      return { base: 'error.notAvailable', status: 'warning' };
    case 'RATE_LIMIT':
      return { base: 'error.rateLimit', status: 'warning' };
    default:
      return { base: 'error.unknown', status: 'error' };
  }
}

function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="dr-center">
      <div className="dr-center__card">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <Brand showTitle={false} />
        </div>
        {children}
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
          <LocaleSwitcher />
        </div>
      </div>
    </div>
  );
}

/**
 * Renders every NON-active session state. This is the route guard's fallback — an unauthenticated
 * applicant NEVER sees a blank authenticated view; they see a calm, plain-language explanation. The
 * applicant can still switch language here (they may need to read the message in their tongue).
 */
export function SessionGate() {
  const { t } = useI18n();
  const { status, error, retry } = useSession();

  if (status === 'exchanging') {
    return (
      <Frame>
        <Spin size="large" />
        <p style={{ marginTop: 20, fontWeight: 600 }}>{t('landing.verifying')}</p>
        <p className="dr-meta">{t('landing.verifyingHint')}</p>
      </Frame>
    );
  }

  if (status === 'no-link') {
    return (
      <Frame>
        <Result
          icon={<MailOutlined />}
          status="info"
          title={t('gate.noLinkTitle')}
          subTitle={t('gate.noLinkBody')}
        />
      </Frame>
    );
  }

  if (status === 'gone') {
    return (
      <Frame>
        <Result
          icon={<LockOutlined />}
          status="warning"
          title={t('gate.usedTitle')}
          subTitle={t('gate.usedBody')}
        />
      </Frame>
    );
  }

  if (status === 'expired') {
    return (
      <Frame>
        <Result
          icon={<ClockCircleOutlined />}
          status="warning"
          title={t('gate.expiredTitle')}
          subTitle={t('gate.expiredBody')}
        />
      </Frame>
    );
  }

  // status === 'error'
  const copy = errorCopy(error?.kind ?? 'UNKNOWN');
  return (
    <Frame>
      <Result
        icon={<WarningOutlined />}
        status={copy.status}
        title={t(`${copy.base}.title`)}
        subTitle={t(`${copy.base}.body`)}
        extra={
          <Button type="primary" icon={<ReloadOutlined />} onClick={retry}>
            {t('common.retry')}
          </Button>
        }
      />
    </Frame>
  );
}
